import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, Users } from "lucide-react";
import { Header } from "../../components/layout/Header";
import { Card } from "../../components/common/Card";
import { Avatar } from "../../components/common/Avatar";
import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import { api } from "../../services/api";
import { User, Friend, Group } from "../../types";
import { COLORS } from "../../config/colors";

type TabId = "friends" | "requests" | "groups";

export default function FriendsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>("friends");
  const [loading, setLoading] = useState(true);

  const [friends, setFriends] = useState<Array<Friend & { user: User }>>([]);
  const [pending, setPending] = useState<Array<Friend & { user: User }>>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [fRes, pRes, gRes] = await Promise.all([
        api.friends.list() as Promise<{ data?: any[] }>,
        api.friends.pending() as Promise<{ data?: any[] }>,
        api.groups.list() as Promise<{ data?: Group[] }>,
      ]);
      setFriends(fRes.data ?? []);
      setPending(pRes.data ?? []);
      setGroups(gRes.data ?? []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handleSearch = async (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = (await api.users.search(term)) as { data?: User[] };
      setSearchResults(res.data ?? []);
    } finally {
      setSearching(false);
    }
  };

  const handleSendRequest = async (uid: string) => {
    await api.friends.sendRequest(uid);
    setSearchResults((r) => r.filter((u) => u.uid !== uid));
  };

  const handleAccept = async (id: string) => {
    await api.friends.accept(id);
    await loadAll();
  };

  const handleDecline = async (id: string) => {
    await api.friends.decline(id);
    setPending((p) => p.filter((r) => r.id !== id));
  };

  return (
    <>
      <Header title='Friends' />

      {/* Tabs */}
      <div className='flex border-b border-gray-200 bg-white sticky top-[60px] md:top-16 z-20'>
        {(["friends", "requests", "groups"] as TabId[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={[
              "flex-1 py-3 text-sm font-semibold capitalize transition-colors",
              tab === t
                ? "text-primary-800 border-b-2 border-primary-800"
                : "text-gray-400",
            ].join(" ")}
          >
            {t}
            {t === "requests" && pending.length > 0 && (
              <span className='ml-1 bg-secondary-400 text-white text-[10px] rounded-full px-1.5 py-0.5'>
                {pending.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className='px-4 py-4 space-y-3'>
        {/* Friends tab */}
        {tab === "friends" && (
          <>
            <Input
              placeholder='Search golfers…'
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              leftIcon={<Search size={16} />}
            />

            {searching && <LoadingSpinner />}

            {searchResults.length > 0 && (
              <div className='space-y-2'>
                <p className='text-xs text-gray-400 font-medium uppercase tracking-wide'>
                  Search Results
                </p>
                {searchResults.map((u) => (
                  <Card key={u.uid} className='flex items-center gap-3'>
                    <Avatar src={u.photoURL} name={u.displayName} />
                    <div className='flex-1'>
                      <p className='font-semibold text-primary-800'>
                        {u.displayName}
                      </p>
                      <p className='text-xs text-gray-400'>{u.email}</p>
                    </div>
                    <Button
                      size='sm'
                      variant='secondary'
                      leftIcon={<UserPlus size={14} />}
                      onClick={() => handleSendRequest(u.uid)}
                    >
                      Add
                    </Button>
                  </Card>
                ))}
              </div>
            )}

            {loading ? (
              <LoadingSpinner />
            ) : friends.length === 0 ? (
              <div className='text-center py-8 text-gray-400'>
                <Users size={36} className='mx-auto mb-2' />
                <p className='text-sm'>
                  No friends yet. Search above to add some!
                </p>
              </div>
            ) : (
              friends.map(({ id, user: u }) => (
                <Card key={id} onClick={() => {}}>
                  <div className='flex items-center gap-3'>
                    <Avatar src={u.photoURL} name={u.displayName} />
                    <div className='flex-1'>
                      <p className='font-semibold text-primary-800'>
                        {u.displayName}
                      </p>
                      <p className='text-xs text-gray-400'>{u.email}</p>
                    </div>
                    {u.isLookingForGame && (
                      <span className='text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium'>
                        LFG
                      </span>
                    )}
                  </div>
                </Card>
              ))
            )}
          </>
        )}

        {/* Requests tab */}
        {tab === "requests" &&
          (loading ? (
            <LoadingSpinner />
          ) : pending.length === 0 ? (
            <div className='text-center py-8 text-gray-400 text-sm'>
              No pending requests.
            </div>
          ) : (
            pending.map(({ id, user: u }) => (
              <Card key={id} accent={COLORS.secondary}>
                <div className='flex items-center gap-3'>
                  <Avatar src={u.photoURL} name={u.displayName} />
                  <div className='flex-1'>
                    <p className='font-semibold text-primary-800'>
                      {u.displayName}
                    </p>
                    <p className='text-xs text-gray-400'>wants to connect</p>
                  </div>
                  <div className='flex gap-1.5'>
                    <Button
                      size='sm'
                      variant='primary'
                      onClick={() => handleAccept(id)}
                    >
                      Accept
                    </Button>
                    <Button
                      size='sm'
                      variant='ghost'
                      onClick={() => handleDecline(id)}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          ))}

        {/* Groups tab */}
        {tab === "groups" && (
          <>
            <Button
              fullWidth
              variant='outline'
              leftIcon={<Users size={16} />}
              onClick={() => navigate("/groups/create")}
            >
              Create Group
            </Button>

            {loading ? (
              <LoadingSpinner />
            ) : groups.length === 0 ? (
              <div className='text-center py-8 text-gray-400 text-sm'>
                No groups yet.
              </div>
            ) : (
              groups.map((g) => (
                <Card
                  key={g.id}
                  accent={COLORS.groups}
                  onClick={() => navigate(`/groups/${g.id}`)}
                >
                  <div className='flex items-center gap-3'>
                    <div className='w-10 h-10 rounded-2xl bg-groups/10 flex items-center justify-center'>
                      <Users size={20} style={{ color: COLORS.groups }} />
                    </div>
                    <div>
                      <p className='font-semibold text-primary-800'>{g.name}</p>
                      <p className='text-xs text-gray-400'>
                        {g.memberIds.length} members
                      </p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </>
        )}
      </div>
    </>
  );
}
